import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import Delta from 'quill-delta';
import useBookingDescription from '../hooks/useBookingDescription';
import { useBookingsContext } from '../contexts/BookingsContext';
import { useSessionContext } from '../contexts/SessionContext';
import useGenerativeAI from '../hooks/useGenerativeAI';
import useSession from '../hooks/useSession';

import {
  TabType,
  IDocumentEditor,
  ISelectedCard,
  INote,
} from '../global/interfaces';
import { IBooking } from '@homeporter/types';

import {
  IUserDocumentTemplate,
  IPromptCard,
  IUserDocumentSection,
  IDelta,
  ITextBlock,
  IChecklist,
  IForm,
} from '@homeporter/types';

import { Quill } from 'react-quill';
import { setConfig } from 'dompurify';
import { useProcessPromptCard } from '../hooks/useProcessPromptCard';
import { useProcessChecklist } from '../hooks/useProcessChecklist';
import { useProcessForm } from '../hooks/useProcessForm';
import { useProcessTranscript } from '../hooks/useProcessTranscript';

const FONT_SIZES = [
  '8px',
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '22px',
  '24px',
  '26px',
  '28px',
  '30px',
];

const Block = Quill.import('blots/block') as any;
const Size: any = Quill.import('attributors/style/size');

class ReadOnlyHeading extends Block {
  static create(value: any): HTMLElement {
    let node = super.create();
    node.setAttribute('data-read-only', 'true');
    return node;
  }
  static formats(node: HTMLElement): any {
    return node.getAttribute('data-read-only') === 'true';
  }
  static value(node: HTMLElement): boolean {
    return node.getAttribute('data-read-only') === 'true';
  }
  format(name: string, value: any) {
    if (name === 'readOnly' && value) {
      this.domNode.setAttribute('data-read-only', 'true');
    } else {
      super.format(name, value);
    }
  }
}

ReadOnlyHeading.blotName = 'readOnlyHeading';
ReadOnlyHeading.tagName = 'h1';

Size.whitelist = FONT_SIZES;
Quill.register(Size, true);
Quill.register(ReadOnlyHeading);

interface _function {
  (sid: string): void;
}

const defaultState: IDocumentEditor = {
  booking: null,
  setBooking: () => {},

  selectedModel: '',
  setSelectedModel: () => {},

  regenerateIndex: undefined,
  setRegenerateIndex: () => {},

  quillRef: { current: null },
  documentRef: { current: null },

  isGeneratingResponse: false,
  errorGeneratingResponse: false,

  fetchDocumentContents: (): void => {},
  isFetchingDocumentContents: false,
  errorFetchingDocumentContents: false,

  saveEnabled: false,
  setSaveEnabled: (): void => {},

  selectedCard: null,
  setSelectedCard: (): void => {},

  saveChanges: (): void => {},

  showGenerateReportPanel: false,
  setShowGenerateReportPanel: (): void => {},

  documentIndex: 0,
  setDocumentIndex: (() => {}) as React.Dispatch<React.SetStateAction<number>>,

  documentsRef: { current: [] } as React.RefObject<IUserDocumentTemplate[]>,
  saveTemplateButtonRef: {
    current: null,
  } as React.MutableRefObject<(() => void) | null>,
  documentSections: [],
  setDocumentSections: () => {},
  documentSection: null,
  setDocumentSection: () => {},
  setDocumentContents: (_: IUserDocumentTemplate[]): void => {},
  appendNewDocument: (_: string) => {},

  isResettingDocumentContents: false,
  errorResettingDocumentContents: false,
  resetDocumentSection: (): void => {},

  activeTab: '',
  setActiveTab: (_: TabType) => {},

  filters: [],
  setFilters: (): void => {},

  defaultMessagesTemplate: '',

  highlightedSids: [],
  setHighlightedSids: (): void => {},

  highlightSentence: undefined,
  setHighlightSentence: (_: _function | undefined) => {},

  scrollTo: undefined,
  setScrollTo: (_: string | undefined) => {},
};

const AIDocumentEditorContext = createContext(defaultState);

export function AIDocumentEditorProvider({
  children,
}: {
  children: JSX.Element;
}) {
  const { profile, context, setContext, documentTemplates, updateDetails } =
    useBookingsContext();
  const {
    setSession,
    transcriptSegments,
    setTranscriptSegments,
    imagesMapRef,
    transcriptAttachments,
  } = useSessionContext();
  const [booking, setBooking] = useState<IBooking | null>(null);
  const { contextRegex } = useBookingDescription();
  const { resetTranscriptAttachments } = useSession();
  const documentsRef = useRef<IUserDocumentTemplate[]>([]);
  const [documentIndex, setDocumentIndex] = useState<number>(0);
  const saveTemplateButtonRef = useRef<(() => void) | null>(null);

  const [documentSections, setDocumentSections] = useState<
    IUserDocumentSection[]
  >([]);
  const [documentSection, setDocumentSection] =
    useState<IUserDocumentSection | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>(
    'Appointment Description',
  );

  const [showGenerateReportPanel, setShowGenerateReportPanel] = useState(false);

  const [highlightedSids, setHighlightedSids] = useState<string[]>([]);
  const [scrollTo, setScrollTo] = useState<string | undefined>(undefined);
  const [highlightSentence, setHighlightSentence] = useState<
    _function | undefined
  >(undefined);

  const appendNewDocument = (title: string) => {
    const newDocument: IUserDocumentTemplate = {
      title: title,
      version: '2',
      dtid: 0,
      template_name: '',
      user_id: profile?.user_id || 0,
      organization_id: 0,
      sections: [
        {
          index: 0,
          //title: 'Section 1',
          context: context || '',
          cards: [],
          deleted: false,
        },
      ],
    };
    documentsRef.current.push(newDocument);
  };

  const setDocumentContents = (documents: IUserDocumentTemplate[]) => {
    documents.forEach((document) => {
      if (!document.title)
        document.title = document.template_name
          .replace(/-/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

      if (document.sections.length === 0) {
        document.sections.push({
          index: 0,
          deleted: false,
          context: '',
          cards: [],
        });
      }

      document.sections.forEach(
        (section: IUserDocumentSection, index: number) => {
          section.index = index;
          if (section.context === '' && !!context) section.context = context;
          if (!section?.cards) section.cards = [];

          section.cards.forEach(
            (card: IPromptCard | ITextBlock | IChecklist | IForm) => {
              if (card.type === 'Block') card.type = 'TextBlock';
              if (card.type === 'Section') card.type = 'Prompt';
              if (card.type === 'CheckList') card.type = 'Checklist';
              if (card.type === 'Prompt') {
                card.undo = card.undo.map((item: { undo: any; redo: any }) => ({
                  undo: new Delta(item.undo),
                  redo: new Delta(item.redo),
                }));

                card.redo = card.redo.map((item) => ({
                  undo: new Delta(item.undo),
                  redo: new Delta(item.redo),
                }));
              }
            },
          );
        },
      );
    });

    setDocumentIndex(0);
    documentsRef.current = documents;
    if (documentsRef.current.length === 0) {
      appendNewDocument('New Document');
    }

    setDocumentSections(documents[documentIndex].sections);
    console.log('::::setDocument section 1...');
    setDocumentSection(documents[documentIndex].sections[0]);
  };

  const {
    documentContents,
    fetchDocumentContents,
    isFetchingDocumentContents,
    errorFetchingDocumentContents,
    saveDocumentContents,

    resettedDocumentContents,
    isResettingDocumentContents,
    errorResettingDocumentContents,
  } = useSession(!booking?.booking_id ? '' : String(booking?.booking_id));

  const resetAttachments = (
    bookingId: number | undefined,
    transcriptAttachments: Record<string, (IDelta | INote)[]>,
  ) => {
    if (!bookingId) return;

    const attachments = Object.entries(transcriptAttachments).flatMap(
      ([id, entries]) =>
        entries.map((entry) =>
          'src' in entry
            ? { id, note: entry as INote }
            : { id, delta: entry as IDelta },
        ),
    );

    resetTranscriptAttachments({
      bookingId,
      attachments,
    }).then((response) => {
      if (response.error) {
        alert('Error resetting transcript attachments');
      }
    });
  };

  useEffect(() => {
    console.log(':+:+:+:+:Loading AIDocumentEditorContect...');

    return () => {
      console.log(':+:+:+:+:Exiting AIDocumentEditorContect...');
    };
  }, []);

  useEffect(() => {
    console.log(
      ':::::useEffect booking from AIDocumentEditorContext booking:',
      booking,
    );

    setSaveEnabled(false);
    setSelectedCard(null);
    setRegenerateIndex(undefined);

    if (booking) {
      fetchDocumentContents();
    } else {
      setSession(booking || undefined);
    }
  }, [booking]);

  useEffect(() => {
    if (!booking || !documentContents) return;

    console.log(':::::useEffect documentContents:', documentContents);

    setSession(booking, documentContents);
    setDocumentContents(documentContents.documents);
  }, [documentContents]);

  const {
    generateRequest,
    setGenerateRequest,
    responseChunk,
    responseChecklist,
    responseForm,
    responseTranscript,
    isGeneratingResponse,
    errorGeneratingResponse,
  } = useGenerativeAI();

  const [regenerateIndex, setRegenerateIndex] = useState<number | undefined>();
  const [filters, setFilters] = useState<string[]>([]);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [selectedCard, setSelectedCard] = useState<ISelectedCard | null>(null);
  const [selectedModel, setSelectedModel] = useState('');

  const quillRef = useRef<any>(null);
  const documentRef = useRef<any>(null);

  useEffect(() => {
    const value =
      !isGeneratingResponse &&
      !documentSection?.cards?.some(
        (card) =>
          card.type === 'TextBlock' ||
          card.type === 'Checklist' ||
          card.type === 'Form',
      ) &&
      (documentSection?.cards
        ?.filter((card) => card.type === 'Prompt')
        ?.every((card) => (card as IPromptCard).response === '') ??
        true);

    setShowGenerateReportPanel(value);
  }, [documentSection, isGeneratingResponse]);

  useEffect(() => {
    if (
      !documentSection ||
      !documentsRef?.current[documentIndex]?.sections[documentSection.index]
    )
      return;

    console.log(':::::useEffect documentSection changed...');

    documentsRef.current[documentIndex].sections[documentSection.index] =
      documentSection;
  }, [documentSection]);

  const defaultMessagesTemplate = `[
    {
      role: promptCard.model === 'gpt-4' ? 'system' : 'user',
      content: \`\${context}. The conversation is captured in the following transcript, \${hasImageDescriptions ? 'image descriptions are included in the sequence they were captured:' : ''}
      \${transcript}\`,
    },
    {
      role: "user",
      content: \`\${prompt}. Use markdown to format the response\`,
    },
  ]`;

  const setBookingContext = (booking: IBooking, _context: string) => {
    if (!context && updateDetails) {
      setContext(_context);
      updateDetails({
        ...booking,
        description: `${booking.description
          .replace(contextRegex, '')
          .trim()}\n\nContext: ${_context}`,
      });
    }
  };

  useProcessPromptCard({
    booking,
    documentSection,
    context,
    transcriptSegments,
    transcriptAttachments,
    imagesMapRef,
    responseChunk,
    setGenerateRequest,
    setBookingContext,
    setSaveEnabled,
    regenerateIndex,
    setRegenerateIndex,
  });

  useProcessChecklist({
    booking,
    documentSection,
    context,
    transcriptSegments,
    transcriptAttachments,
    setGenerateRequest,
    setBookingContext,
    setSaveEnabled,
    regenerateIndex,
    setRegenerateIndex,
    responseChecklist,
    setDocumentSection,
    setTranscriptSegments,
    setScrollTo,
    resetAttachments,
    generateRequest,
  });

  useProcessForm({
    booking,
    documentSection,
    context,
    transcriptSegments,
    transcriptAttachments,
    setGenerateRequest,
    setBookingContext,
    setSaveEnabled,
    regenerateIndex,
    setRegenerateIndex,
    responseForm,
    setDocumentSection,
    setTranscriptSegments,
    setScrollTo,
    resetAttachments,
    generateRequest,
    imagesMapRef,
  });

  useProcessTranscript({
    booking,
    documentSection,
    context,
    transcriptSegments,
    transcriptAttachments,
    setGenerateRequest,
    selectedModel,
    regenerateIndex,
    setRegenerateIndex,
    responseTranscript,
    setTranscriptSegments,
    setScrollTo,
    resetAttachments,
    generateRequest,
  });

  const saveChanges = () => {
    console.log('::::saveChanges: booking_id: ' + booking?.booking_id);

    if (!booking?.booking_id) return alert('selected booking is empty');
    if (selectedCard) setSelectedCard(null);

    Promise.resolve().then(() => {
      saveDocumentContents({
        bookingId: booking?.booking_id,
        documents: documentsRef.current,
      });
    });
  };

  const resetDocumentSection = () => {
    console.log(':::::resetDocumentSection:', booking?.booking_id);
    if (
      !booking?.booking_id ||
      !documentsRef?.current ||
      !documentSection?.cards
    )
      return;

    setSelectedCard(null);
    //resetDocumentSectionContents(String(booking?.booking_id));

    const selectedTemplate = documentTemplates.find(
      (doc) =>
        doc.template_name === documentsRef.current[documentIndex].template_name,
    );

    if (false && selectedTemplate) {
      //this results in bad ui, should remove template id from UserDocument object instead
      //documentsRef.current[documentIndex].sections = JSON.parse(
      //  JSON.stringify(selectedTemplate.sections),
      //);
      //console.log('::::setDocument section...'); setDocumentSection(documentsRef.current[documentIndex].sections[0]);
    } else {
      const newSection: IUserDocumentSection = {
        index: documentSections.length,
        deleted: false,
        context,
        cards: [],
      };

      documentsRef.current[documentIndex].sections = [newSection];
      console.log('::::setDocument section 2...');
      setDocumentSection(newSection);
    }

    setShowGenerateReportPanel(true);
    setSaveEnabled(true);
  };

  useEffect(() => {
    if (!resettedDocumentContents) return;

    console.log(
      '++++++::::inside useEffect resettedDocumentContents... ',
      resettedDocumentContents,
    );

    //setDocumentSectionCards(resettedDocumentContents);
  }, [resettedDocumentContents]);

  /*
  AWS Signed URLs
  const AWS = require('aws-sdk');

  // Configure AWS SDK
  const s3 = new AWS.S3({
    accessKeyId: 'your-access-key-id',
    secretAccessKey: 'your-secret-access-key',
    region: 'your-region'
  });

  // Generate a signed URL
  const params = {
    Bucket: 'your-bucket-name',
    Key: 'path-to-your-image/filename.webp',
    Expires: 60 // The URL will be valid for 60 seconds
  };

  const signedUrl = s3.getSignedUrl('getObject', params);
  console.log(signedUrl);


  const response = await axios({
    url: signedUrl, // Use the signed URL generated by AWS
    method: 'GET',
    responseType: 'blob'
  });

*/

  return (
    <AIDocumentEditorContext.Provider
      value={{
        booking,
        setBooking,

        selectedModel,
        setSelectedModel,
        regenerateIndex,
        setRegenerateIndex,

        quillRef,
        documentRef,

        isGeneratingResponse,
        errorGeneratingResponse,

        fetchDocumentContents,
        isFetchingDocumentContents,
        errorFetchingDocumentContents,

        saveEnabled,
        setSaveEnabled,

        selectedCard,
        setSelectedCard,

        saveChanges,

        showGenerateReportPanel,
        setShowGenerateReportPanel,

        documentIndex,
        setDocumentIndex,

        documentsRef,
        saveTemplateButtonRef,

        documentSections,
        setDocumentSections,
        documentSection,
        setDocumentSection,
        setDocumentContents,
        appendNewDocument,

        isResettingDocumentContents,
        errorResettingDocumentContents,
        resetDocumentSection,

        activeTab,
        setActiveTab,

        filters,
        setFilters,
        defaultMessagesTemplate,

        highlightedSids,
        setHighlightedSids,
        highlightSentence,
        setHighlightSentence,
        scrollTo,
        setScrollTo,
      }}>
      {children}
    </AIDocumentEditorContext.Provider>
  );
}

export function useAIDocumentEditorContext() {
  return useContext(AIDocumentEditorContext);
}
